import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';
import { getClaimSearchStr } from '../utils/common';

import type {
  AdminUpdateClaimStatusPayload,
  BulkMarkNotesUnbillablePayload,
  BulkUnbillableSummary,
  IClaim,
  IClaimFollowUpNote,
  IClaimForm1500Data,
  IClaimInvoiceHistory,
} from '../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'claims';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ClaimsState>) => createExtraReducers(builder),
});

export interface ClaimsState {
  list: IClaim[];
  current: IClaim | null;
  claimInvoiceHistoryList: IClaimInvoiceHistory[];
  fileUrls: Array<string>;
  claimPreview: IClaimForm1500Data | null;
  bulkUnbillableSummary: BulkUnbillableSummary | null;
  error: any;
  loading: boolean;
}

function createInitialState(): ClaimsState {
  return {
    list: [],
    fileUrls: [],
    claimInvoiceHistoryList: [],
    claimPreview: null,
    bulkUnbillableSummary: null,
    current: null,
    error: null,
    loading: false,
  };
}

function createReducers() {
  return { clearState, updateState, clearBulkUnbillableSummary };

  function clearState(state: ClaimsState) {
    state.list = [];
    state.current = null;
    state.fileUrls = [];
    state.claimInvoiceHistoryList = [];
    state.claimPreview = null;
    state.bulkUnbillableSummary = null;
    state.loading = false;
    state.error = null;
  }

  function updateState(
    state: ClaimsState,
    action: PayloadAction<{ key: keyof ClaimsState; data: ClaimsState[keyof ClaimsState] }>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  // Clear only the bulk-unbillable summary so the admin page starts clean on
  // (re)mount. Redux state survives navigation, so without this a stale "Result"
  // block (and failures download) would render on return before a new upload.
  function clearBulkUnbillableSummary(state: ClaimsState) {
    state.bulkUnbillableSummary = null;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/claims`;

  return {
    createClaim: createClaim(),
    getClaimDocumentUrls: getClaimDocumentUrls(),
    updateClaim: updateClaim(),
    createClaimPreview: createClaimPreview(),
    getClaim: getClaim(),
    getList: getList(),
    resubmitClaim: resubmitClaim(),
    deleteClaim: deleteClaim(),
    adminDeleteClaim: adminDeleteClaim(),
    createFollowUpNote: createFollowUpNote(),
    updatePaymentStatus: updatePaymentStatus(),
    createServiceLinePayment: createServiceLinePayment(),
    updateServiceLinePayment: updateServiceLinePayment(),
    deleteServiceLinePayment: deleteServiceLinePayment(),
    certifyClaim: certifyClaim(),
    createEobDocument: createEobDocument(),
    deleteEobDocument: deleteEobDocument(),
    submitClaimToTebra: submitClaimToTebra(),
    bulkMarkNotesUnbillable: bulkMarkNotesUnbillable(),
    adminUpdateClaimStatus: adminUpdateClaimStatus(),
  };

  function createClaim() {
    return createAsyncThunk(
      `${name}/createClaim`,
      async ({ data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post({ url: `${baseUrl}/`, getAccessTokenSilently }, data);
      },
    );
  }

  function createClaimPreview() {
    return createAsyncThunk(
      `${name}/createClaimPreview`,
      async ({ data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/preview/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function resubmitClaim() {
    return createAsyncThunk(
      `${name}/resubmitClaim`,
      async ({ data, getAccessTokenSilently, claimId }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/${claimId}/resubmit/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function updateClaim() {
    return createAsyncThunk(
      `${name}/updateClaim`,
      async ({ data, getAccessTokenSilently, claimId }: any) => {
        return await fetchWrapper.patch(
          { url: `${baseUrl}/${claimId}/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function createFollowUpNote() {
    return createAsyncThunk(
      `${name}/createFollowUpNote`,
      async ({ data, getAccessTokenSilently, claimId }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/${claimId}/follow_up_notes/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function getClaim() {
    return createAsyncThunk(
      `${name}/getClaim`,
      async ({ claimId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({ url: `${baseUrl}/${claimId}/`, getAccessTokenSilently });
      },
    );
  }

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += `?${getClaimSearchStr(key, params[key])}`;
            } else {
              baseSearch += `&${getClaimSearchStr(key, params[key])}`;
            }
          });
        }
        return await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently, signal });
      },
    );
  }

  function getClaimDocumentUrls() {
    return createAsyncThunk(
      `${name}/getClaimDocumentUrls`,
      async ({ claimId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/${claimId}/procedures/download/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function deleteClaim() {
    return createAsyncThunk(
      `${name}/deleteClaim`,
      async ({ claimId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.delete({ url: `${baseUrl}/${claimId}/`, getAccessTokenSilently });
      },
    );
  }

  // Admin-only force delete: hits the guardrail-free admin endpoint (deletes a
  // claim in any status), separate from the status-guarded deleteClaim above.
  function adminDeleteClaim() {
    return createAsyncThunk(
      `${name}/adminDeleteClaim`,
      async ({ claimId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.delete({
          url: `${baseUrl}/admin/${claimId}/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function updatePaymentStatus() {
    return createAsyncThunk(
      `${name}/updatePaymentStatus`,
      async ({ data, getAccessTokenSilently, claimId }: any) => {
        return await fetchWrapper.patch(
          { url: `${baseUrl}/${claimId}/payment_status/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function createServiceLinePayment() {
    return createAsyncThunk(
      `${name}/createServiceLinePayment`,
      async ({ data, getAccessTokenSilently, claimId, serviceLineId }: any) => {
        return await fetchWrapper.post(
          {
            url: `${baseUrl}/${claimId}/service_lines/${serviceLineId}/payments/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function updateServiceLinePayment() {
    return createAsyncThunk(
      `${name}/updateServiceLinePayment`,
      async ({ data, getAccessTokenSilently, claimId, serviceLineId, paymentId }: any) => {
        return await fetchWrapper.patch(
          {
            url: `${baseUrl}/${claimId}/service_lines/${serviceLineId}/payments/${paymentId}/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function deleteServiceLinePayment() {
    return createAsyncThunk(
      `${name}/deleteServiceLinePayment`,
      async ({ getAccessTokenSilently, claimId, serviceLineId, paymentId }: any) => {
        return await fetchWrapper.delete({
          url: `${baseUrl}/${claimId}/service_lines/${serviceLineId}/payments/${paymentId}/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function certifyClaim() {
    return createAsyncThunk(
      `${name}/certifyClaim`,
      async ({ getAccessTokenSilently, claimId, data }: any) => {
        return await fetchWrapper.put(
          {
            url: `${baseUrl}/${claimId}/certify_claim/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function createEobDocument() {
    return createAsyncThunk(
      `${name}/createEobDocument`,
      async ({ getAccessTokenSilently, claimId, data }: any) => {
        return await fetchWrapper.post(
          {
            url: `${baseUrl}/${claimId}/eob_documents/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function deleteEobDocument() {
    return createAsyncThunk(
      `${name}/deleteEobDocument`,
      async ({ getAccessTokenSilently, claimId, eobDocumentId }: any) => {
        return await fetchWrapper.delete({
          url: `${baseUrl}/${claimId}/eob_documents/${eobDocumentId}/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function submitClaimToTebra() {
    return createAsyncThunk(
      `${name}/submitClaimToTebra`,
      async ({ getAccessTokenSilently, claimId }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/${claimId}/tebra/submit/`, getAccessTokenSilently },
          {},
        );
      },
    );
  }

  function bulkMarkNotesUnbillable() {
    return createAsyncThunk(
      `${name}/bulkMarkNotesUnbillable`,
      async ({ file, getAccessTokenSilently }: BulkMarkNotesUnbillablePayload) => {
        const formData = new FormData();
        formData.append('file', file);

        return await fetchWrapper.post(
          { url: `${baseUrl}/admin/bulk-unbillable`, getAccessTokenSilently },
          formData,
          'multipart/form-data',
        );
      },
    );
  }

  function adminUpdateClaimStatus() {
    return createAsyncThunk(
      `${name}/adminUpdateClaimStatus`,
      async ({ claimId, data, getAccessTokenSilently }: AdminUpdateClaimStatusPayload) => {
        return await fetchWrapper.patch(
          { url: `${baseUrl}/admin/${claimId}/status/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ClaimsState>) {
  const {
    createClaim,
    createClaimPreview,
    resubmitClaim,
    updateClaim,
    getClaim,
    getClaimDocumentUrls,
    getList,
    deleteClaim,
    adminDeleteClaim,
    createFollowUpNote,
    updatePaymentStatus,
    createServiceLinePayment,
    updateServiceLinePayment,
    deleteServiceLinePayment,
    certifyClaim,
    createEobDocument,
    deleteEobDocument,
    submitClaimToTebra,
    bulkMarkNotesUnbillable,
    adminUpdateClaimStatus,
  } = extraActions;

  builder
    // createClaim()
    .addCase(createClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createClaim.fulfilled, (state: ClaimsState, action: PayloadAction<IClaim>) => {
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(createClaim.rejected, (state: ClaimsState, action: any) => {
      state.current = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'create-claim-error' });
    })
    // createClaimPreview()
    .addCase(createClaimPreview.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createClaimPreview.fulfilled,
      (state: ClaimsState, action: PayloadAction<IClaimForm1500Data>) => {
        state.claimPreview = action.payload;
        state.loading = false;
      },
    )
    .addCase(createClaimPreview.rejected, (state: ClaimsState, action: any) => {
      state.claimPreview = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // resubmitClaim()
    .addCase(resubmitClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(resubmitClaim.fulfilled, (state: ClaimsState, action: PayloadAction<IClaim>) => {
      state.current = action.payload;
      state.loading = false;
      toast.success('Claim has been resubmitted successfully');
    })
    .addCase(resubmitClaim.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'create-claim-error' });
    })
    // updateClaim()
    .addCase(updateClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateClaim.fulfilled, (state: ClaimsState, action: PayloadAction<IClaim>) => {
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(updateClaim.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // getClaim()
    .addCase(getClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getClaim.fulfilled, (state: ClaimsState, action: PayloadAction<IClaim>) => {
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(getClaim.rejected, (state: ClaimsState, action: any) => {
      state.current = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'get-claim-error' });
    })

    // getClaimDocumentUrls()
    .addCase(getClaimDocumentUrls.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getClaimDocumentUrls.fulfilled,
      (state: ClaimsState, action: PayloadAction<Array<string>>) => {
        state.fileUrls = action.payload;
        state.loading = false;
      },
    )
    .addCase(getClaimDocumentUrls.rejected, (state: ClaimsState, action: any) => {
      state.fileUrls = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // getList()
    .addCase(getList.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: ClaimsState, action: any) => {
      const { invoice_history_attributes_only } = action?.meta?.arg?.params ?? {};
      if (invoice_history_attributes_only) {
        state.claimInvoiceHistoryList = action.payload;
      } else {
        state.list = action.payload;
      }
      state.loading = false;
    })
    .addCase(getList.rejected, (state: ClaimsState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'get-claims-list-error' });
    })

    // deleteClaim()
    .addCase(deleteClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteClaim.fulfilled, (state: ClaimsState, action: PayloadAction<IClaim>) => {
      state.current = action.payload;
      state.loading = false;
      toast.success('Claim deleted successfully', { toastId: 'delete-claim-success' });
    })
    .addCase(deleteClaim.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'delete-claim-error' });
    })
    // adminDeleteClaim()
    .addCase(adminDeleteClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(adminDeleteClaim.fulfilled, (state: ClaimsState) => {
      state.loading = false;
      toast.success('Claim deleted successfully', { toastId: 'admin-delete-claim-success' });
    })
    .addCase(adminDeleteClaim.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'admin-delete-claim-error' });
    })
    // createFollowUpNote()
    .addCase(createFollowUpNote.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createFollowUpNote.fulfilled,
      (state: ClaimsState, action: PayloadAction<IClaimFollowUpNote>) => {
        if (state.current?.claim_follow_up_notes) {
          state.current.claim_follow_up_notes = [
            action.payload,
            ...state.current.claim_follow_up_notes,
          ];
        }
        state.loading = false;
        toast.success('Follow-up note created');
      },
    )
    .addCase(createFollowUpNote.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updatePaymentStatus()
    .addCase(updatePaymentStatus.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updatePaymentStatus.fulfilled, (state: ClaimsState, action: PayloadAction<IClaim>) => {
      state.current = action.payload;
      state.loading = false;
      toast.success('Payment status updated successfully');
    })
    .addCase(updatePaymentStatus.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // createServiceLinePayment()
    .addCase(createServiceLinePayment.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createServiceLinePayment.fulfilled, (state: ClaimsState, action: any) => {
      state.current = {
        ...state.current,
        ...action.payload.claim_payment_details,
        service_lines: state.current?.service_lines.map((sl) => {
          const {
            claim_payment_details: _claim_payment_details,
            service_line,
            ...paymentDetails
          } = action.payload;
          if (sl.id === action.meta.arg.serviceLineId) {
            const updatedSl = {
              ...sl,
              ...service_line,
              payments: [...sl.payments, paymentDetails],
            };
            return updatedSl;
          }
          return sl;
        }) ?? [],
      };
      state.loading = false;
    })
    .addCase(createServiceLinePayment.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateServiceLinePayment()
    .addCase(updateServiceLinePayment.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateServiceLinePayment.fulfilled, (state: ClaimsState, action: any) => {
      state.current = {
        ...state.current,
        ...action.payload.claim_payment_details,
        service_lines: state.current?.service_lines.map((sl) => {
          const {
            claim_payment_details: _claim_payment_details,
            service_line,
            ...paymentDetails
          } = action.payload;
          if (sl.id === action.meta.arg.serviceLineId) {
            const updatedSl = {
              ...sl,
              ...service_line,
              payments: sl.payments.map((payment) => {
                if (payment.id === action.meta.arg.paymentId) {
                  return paymentDetails;
                }
                return payment;
              }),
            };
            return updatedSl;
          }
          return sl;
        }) ?? [],
      };
      state.loading = false;
    })
    .addCase(updateServiceLinePayment.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // deleteServiceLinePayment()
    .addCase(deleteServiceLinePayment.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteServiceLinePayment.fulfilled, (state: ClaimsState, action: any) => {
      const { claim_payment_details, service_line } = action.payload;
      state.current = {
        ...state.current,
        ...claim_payment_details,
        service_lines: state.current?.service_lines.map((sl) => {
          if (sl.id === action.meta.arg.serviceLineId) {
            const updatedSl = {
              ...sl,
              payments: sl.payments.filter((payment) => payment.id !== action.meta.arg.paymentId),
              ...service_line,
            };
            return updatedSl;
          }
          return sl;
        }) ?? [],
      };
      state.loading = false;
    })
    .addCase(deleteServiceLinePayment.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    //certifyClaim()
    .addCase(certifyClaim.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(certifyClaim.fulfilled, (state: ClaimsState, action: any) => {
      const {
        is_invoiceable,
        date_last_certified,
        utc_offset_date_last_certified,
        last_certified_by,
      } = action.payload;
      if (state.current) {
        state.current.is_invoiceable = is_invoiceable;
        state.current.date_last_certified = date_last_certified;
        state.current.utc_offset_date_last_certified = utc_offset_date_last_certified;
        state.current.last_certified_by = last_certified_by;
      }
      state.loading = false;
      toast.success(
        state.current?.is_invoiceable
          ? 'Claim marked as Ready to Invoice'
          : 'Claim marked as Not Ready to Invoice',
      );
    })
    .addCase(certifyClaim.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // createEobDocument()
    .addCase(createEobDocument.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createEobDocument.fulfilled, (state: ClaimsState, action: any) => {
      const eob_document = action.payload;
      const updatedEobDocuments = [...(state.current?.eob_documents || []), eob_document];
      if (state.current) {
        state.current.eob_documents = updatedEobDocuments;
      }
      state.loading = false;
    })

    .addCase(createEobDocument.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // deleteEobDocument()
    .addCase(deleteEobDocument.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteEobDocument.fulfilled, (state: ClaimsState, action: any) => {
      if (state.current) {
        state.current.eob_documents = action.payload;
      }
      state.loading = false;
    })
    .addCase(deleteEobDocument.rejected, (state: ClaimsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // submitClaimToTebra()
    .addCase(submitClaimToTebra.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(submitClaimToTebra.fulfilled, (state: ClaimsState, action: any) => {
      state.loading = false;
      // Don't overwrite state.current; the backend response doesn't include the full claim.
      // Instead refetch or just show success.
      toast.success(`Claim submitted to Tebra (encounter ${action.payload.tebra_encounter_id})`);
    })
    .addCase(submitClaimToTebra.rejected, (state: ClaimsState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message, { toastId: 'tebra-submit-error' });
    })
    // bulkMarkNotesUnbillable()
    .addCase(bulkMarkNotesUnbillable.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
      state.bulkUnbillableSummary = null;
    })
    .addCase(
      bulkMarkNotesUnbillable.fulfilled,
      (state: ClaimsState, action: PayloadAction<BulkUnbillableSummary>) => {
        state.bulkUnbillableSummary = action.payload;
        state.loading = false;
        const { claims_created: created, rows_failed: failed } = action.payload;
        if (failed > 0) {
          toast.warning(`Bulk unbillable: ${created} created, ${failed} failed`, {
            toastId: 'bulk-unbillable-result',
          });
        } else {
          toast.success(`Bulk unbillable upload completed: ${created} created`, {
            toastId: 'bulk-unbillable-result',
          });
        }
      },
    )
    .addCase(bulkMarkNotesUnbillable.rejected, (state: ClaimsState, action) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message, { toastId: 'bulk-unbillable-error' });
    })

    // adminUpdateClaimStatus()
    .addCase(adminUpdateClaimStatus.pending, (state: ClaimsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      adminUpdateClaimStatus.fulfilled,
      (state: ClaimsState, action: PayloadAction<IClaim>) => {
        state.current = action.payload;
        state.loading = false;
        toast.success('Claim status updated', { toastId: 'admin-claim-status-success' });
      },
    )
    .addCase(adminUpdateClaimStatus.rejected, (state: ClaimsState, action) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'admin-claim-status-error' });
    });
}

export const claimsActions = { ...slice.actions, ...extraActions };
export const claimsReducer = slice.reducer;
